import { useCallback, useContext, useEffect, useMemo, useState, type CSSProperties } from "react";
import { ApiError } from "../../api/client";
import { listUnconfirmed, prepare, replayAll, resend } from "../../api/pending";
import type { BillAnswer, BillPart, LineRequest, Order, PrintNotice, Product } from "../../api/types";
import { AllergenCard } from "../../features/allergens/AllergenCard";
import { unroutedToast } from "../../features/printing/print-notices";
import { toLineRequest } from "../../features/sale/cart";
import { defaultVariant, lineFor, oneTap, shownVariant } from "../../features/sale/quick-entry";
import { useQuote } from "../../features/sale/useQuote";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { AppIcon, type AppIconName } from "../AppIcon";
import { AppColumn, AppPage, WideScreen } from "../AppScreens";
import type { AppEnv } from "../AppShell";
import { appDrafts, type Draft, type DraftLine, type Drafts } from "../app-storage";
import { Bill } from "./Bill";
import { MoveView } from "./MoveView";
import { OptionsView } from "./OptionsView";
import { OrderView } from "./OrderView";
import { PayView } from "./PayView";
import { PickView, type PickEntry } from "./PickView";
import { TablesView } from "./TablesView";
import { takeawayRequest } from "./TakeawayCustomer";
import { WaiterHome } from "./WaiterHome";
import { useWaiterData } from "./useWaiterData";
import {
  addToDraft,
  changeQuantity,
  channelOf,
  kitchenCopies,
  kitchenPrinters,
  printingPc,
  receiptPrinter,
  spotKey,
  type Spot,
} from "./waiter";

type Tab = "home" | "tables" | "takeaway";

/** Where the waiter is (prototype CP.op): a table or the takeaway, and what is open on it. */
type View =
  | { name: "order"; spot: Spot }
  | { name: "pick"; spot: Spot; category: string | null; query: string }
  | { name: "options"; spot: Spot; product: Product; variant: string | undefined; category: string | null }
  | { name: "pay"; spot: Spot }
  | { name: "move"; spot: Extract<Spot, { kind: "table" }> };

const EMPTY_DRAFT: Draft = { lines: [], name: "" };
const MINUTE_MS = 60_000;
/** Unconfirmed sales and payments are sent again by themselves (ADR 0006). */
const REPLAY_MS = 15_000;

/** The bill shown after "🧾 Rekening", with where it was printed. */
interface ShownBill {
  order: Order;
  prints: PrintNotice[];
  at: string;
  /** The verdeling at the bottom, when the waiter made one. */
  parts: BillPart[];
}

/**
 * The waiter's app (prototype renderPhone for the ober, obBody, obBind): Start (Furkan, 08/10/2026: his
 * card, the orders of today, another payment method), Tafels and Afhaal, in a bar at the bottom; a table opens
 * its bill, the products, the options, Afrekenen and Tafel verplaatsen. On an iPad the bill stays on the
 * left and the products on the right (prototype ipad2).
 */
export function WaiterApp({ env }: { env: AppEnv }) {
  const { api, status, session, toast, report } = env;
  const data = useWaiterData(env);
  const {
    menu: menuResponse,
    floor,
    orders,
    openTables,
    soldOut,
    promotions,
    general,
    bankQrAutomatic,
    receiptQr,
  } = data;
  const deviceType = status.device.type;
  const wide = useContext(WideScreen);
  const [tab, setTab] = useState<Tab>("home");
  const [view, setView] = useState<View | null>(null);
  const [drafts, setDrafts] = useState<Drafts>(() => appDrafts.read());
  const [lastCategory, setLastCategory] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bill, setBill] = useState<ShownBill | null>(null);
  const [allergens, setAllergens] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [pending, setPending] = useState(() => listUnconfirmed().length);

  useEffect(() => {
    appDrafts.save(drafts);
  }, [drafts]);
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, MINUTE_MS / 2);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const reloadOrders = orders.reload;
  const replay = useCallback(async () => {
    const left = await replayAll(api, {
      onSaved: (label) => {
        toast(label);
      },
      onRefused: report,
    });
    setPending(left);
    reloadOrders();
  }, [api, toast, report, reloadOrders]);
  useEffect(() => {
    const timer = setInterval(() => {
      const count = listUnconfirmed().length;
      setPending(count);
      if (count > 0) void replay();
    }, REPLAY_MS);
    return () => {
      clearInterval(timer);
    };
  }, [replay]);

  const draftOf = (spot: Spot): Draft => drafts[spotKey(spot)] ?? EMPTY_DRAFT;
  const setDraft = (spot: Spot, change: (draft: Draft) => Draft) => {
    setDrafts((current) => {
      const next = change(current[spotKey(spot)] ?? EMPTY_DRAFT);
      const { [spotKey(spot)]: _old, ...rest } = current;
      return next.lines.length === 0 && next.name === "" ? rest : { ...rest, [spotKey(spot)]: next };
    });
  };
  const withDraft = useMemo(
    () =>
      new Set(
        Object.entries(drafts).flatMap(([key, draft]) =>
          key.startsWith("t:") && draft.lines.length > 0 ? [key.slice(2)] : [],
        ),
      ),
    [drafts],
  );

  // the spot on screen: its open order, what is not sent yet and what that comes to (server prices)
  const spot = view?.spot ?? (tab === "takeaway" ? ({ kind: "takeaway" } as const) : null);
  const order = spot?.kind === "table" ? (openTables.get(spot.table) ?? null) : null;
  const draft = spot === null ? EMPTY_DRAFT : draftOf(spot);
  const sentRequests = (order?.lines ?? []).flatMap((line) => (line.request ? [line.request] : []));
  const quoteLines = draft.lines.length > 0 ? [...sentRequests, ...draft.lines.map(toLineRequest)] : [];
  const priced = useQuote(
    api,
    spot !== null && quoteLines.length > 0 ? { channel: channelOf(spot), lines: quoteLines } : null,
    JSON.stringify([promotions, menuResponse?.version]),
  );
  const total =
    draft.lines.length === 0
      ? order === null
        ? null
        : order.totalCents - order.paidCents
      : priced.fresh && priced.quote
        ? priced.quote.totalCents - (order?.paidCents ?? 0)
        : null;

  if (menuResponse === null) return <AppPage head={env.head}>{null}</AppPage>;
  if (menuResponse.menu === null) {
    return (
      <AppPage head={env.head}>
        <p className="proto ph-empty">{t.errors.menuMissing}</p>
      </AppPage>
    );
  }
  const menu = menuResponse.menu;
  const rules = menuResponse.rules;

  const label = (line: LineRequest) => {
    const product = menu.products[line.productId];
    const variant = shownVariant(product, line);
    return `${product?.name ?? line.productId}${variant === null ? "" : ` ${variant}`}`;
  };

  /** Back from where the waiter is (prototype back()). */
  const back = () => {
    if (view === null) return;
    if (view.name === "order" || (wide && view.name === "pick")) {
      setView(null);
    } else if (view.name === "options") {
      setView({ name: "pick", spot: view.spot, category: view.category, query: "" });
    } else if (view.spot.kind === "takeaway" && tab === "takeaway" && !wide) {
      setView(null);
    } else {
      setView(
        wide
          ? { name: "pick", spot: view.spot, category: lastCategory, query: "" }
          : { name: "order", spot: view.spot },
      );
    }
  };
  /** The bill of a spot, after adding a product or sending: on an iPad the products stay open. */
  const showSpot = (at: Spot) => {
    setView(wide ? { name: "pick", spot: at, category: lastCategory, query: "" } : { name: "order", spot: at });
  };

  const addLine = (at: Spot, line: LineRequest) => {
    setDraft(at, (current) => ({ ...current, lines: addToDraft(current.lines, line, crypto.randomUUID()) }));
    toast(t.tafoApp.waiter.added(label(line)));
    showSpot(at);
  };

  /** A product tapped (prototype data-op): one tap for a drink or a product without choices, else the options. */
  const onProduct = (at: Spot, entry: PickEntry, category: string | null) => {
    const { product } = entry;
    const variant = entry.variant ?? defaultVariant(product);
    if (oneTap(menu, product) || (entry.variant !== undefined && (product.modifierGroupIds ?? []).length === 0)) {
      addLine(at, lineFor(product, { variant, quantity: 1 }));
      return;
    }
    setView({ name: "options", spot: at, product, variant, category });
  };

  /**
   * "🍳 Naar keuken" (prototype obSendKitchen): a free table becomes an order, an open table gets the new
   * lines on top (BIJBESTELLING). A table changed on another device meanwhile is read again and the new
   * lines go on top of that.
   */
  const sendTable = async (at: Extract<Spot, { kind: "table" }>): Promise<Order | null> => {
    const lines = draftOf(at).lines.map(toLineRequest);
    if (lines.length === 0) return openTables.get(at.table) ?? null;
    const open = openTables.get(at.table) ?? null;
    const title = t.tafoApp.waiter.table(at.table);
    const add = async (current: Order) =>
      resend<Order>(
        api,
        prepare(
          `/orders/${current.id}`,
          {
            expectedRevision: current.revision,
            lines: [...current.lines.flatMap((line) => (line.request ? [line.request] : [])), ...lines],
          },
          title,
          { method: "PUT" },
        ),
      );
    let saved: Order;
    let extra = open !== null;
    try {
      saved =
        open === null
          ? await resend<Order>(api, prepare("/orders", { channel: "table", table: at.table, lines }, title))
          : await add(open);
    } catch (error) {
      // another waiter or the till opened or changed this table meanwhile: on top of what is there now
      if (!(error instanceof ApiError) || (error.code !== "order_changed" && error.code !== "table_occupied"))
        throw error;
      const fresh = (await api.request<Order[]>("GET", "/orders", { retry: true })).find(
        (candidate) => candidate.channel === "table" && candidate.status === "open" && candidate.table === at.table,
      );
      if (!fresh) throw error;
      extra = true;
      saved = await add(fresh);
    }
    orders.upsert(saved);
    setDraft(at, (current) => ({ ...current, lines: [] }));
    const printers = kitchenPrinters(saved.prints);
    const pc = printingPc(saved.prints);
    toast(
      t.tafoApp.waiter.kitchen(
        at.table,
        extra,
        printers.length === 0
          ? t.tafoApp.waiter.kitchenNoPrinter
          : `${pc === null ? "" : `${t.tafoApp.waiter.viaShort(pc)} → `}${printers.join(", ")}`,
      ),
    );
    // nothing printed in the kitchen because no kitchen printer is set up: in red (Papermoon, 08/10/2026)
    const missing = unroutedToast(saved.prints);
    if (missing !== null) toast(missing, "bad");
    return saved;
  };

  const run = async (work: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await work();
    } catch (error) {
      if (error instanceof ApiError && error.retryable) {
        toast(t.tafoApp.pay.deferred, "bad");
        setPending(listUnconfirmed().length);
      } else {
        report(error);
      }
    } finally {
      setBusy(false);
    }
  };

  const send = (at: Extract<Spot, { kind: "table" }>) =>
    run(async () => {
      await sendTable(at);
      setView(null);
      setTab("tables");
    });

  /** "🧾 Rekening": what is new goes to the kitchen first, then the bill is printed and shown. */
  const printBill = (at: Extract<Spot, { kind: "table" }>) =>
    run(async () => {
      const current = await sendTable(at);
      if (current === null) return;
      showBill(await api.request<BillAnswer>("POST", `/orders/${current.id}/bill`));
    });

  /** The bill printed (with a QR or a verdeling from Afrekenen): said where, and shown on the screen. */
  const showBill = (answer: BillAnswer) => {
    const printed = receiptPrinter(answer.prints, "bill");
    toast(
      printed === null
        ? t.tafoApp.waiter.billShown
        : t.tafoApp.waiter.billPrinted(`${t.tafoApp.waiter.viaPc(printingPc(answer.prints))} → ${printed.printer}`),
    );
    orders.upsert(answer.order);
    setBill({ order: answer.order, prints: answer.prints, at: new Date().toISOString(), parts: answer.parts ?? [] });
  };

  /** "💶 Afrekenen": at a table what is new goes to the kitchen first (prototype). */
  const openPay = (at: Spot) =>
    run(async () => {
      if (at.kind === "table") await sendTable(at);
      setView({ name: "pay", spot: at });
    });

  /** "Betalen bij afhalen": to the kitchen now, paid at the till from the Wachtlijst (prototype). */
  const later = () =>
    run(async () => {
      const takeaway = draftOf({ kind: "takeaway" });
      const name = takeaway.name.trim();
      const made = await resend<Order>(
        api,
        prepare(
          "/orders",
          {
            channel: "takeaway",
            lines: takeaway.lines.map(toLineRequest),
            ...takeawayRequest(takeaway),
          },
          t.tafoApp.waiter.takeaway,
        ),
      );
      orders.upsert(made);
      setDraft({ kind: "takeaway" }, () => EMPTY_DRAFT);
      toast(t.tafoApp.pay.takeawayLater(name, kitchenCopies(made.prints)));
      setView(null);
    });

  /** "⇄ Tafel verplaatsen": the order and what was not sent yet go to the free table. */
  const move = (from: string, to: string) =>
    run(async () => {
      const open = openTables.get(from);
      let kitchen = false;
      if (open) {
        const moved = await api.request<Order>("POST", `/orders/${open.id}/move`, { body: { table: to } });
        orders.upsert(moved);
        kitchen = kitchenPrinters(moved.prints).length > 0;
      }
      setDrafts((current) => {
        const { [`t:${from}`]: moving, ...rest } = current;
        return moving ? { ...rest, [`t:${to}`]: moving } : rest;
      });
      toast(kitchen ? t.tables.movedKitchen(from, to) : t.tables.moved(from, to));
      showSpot({ kind: "table", table: to });
    });

  const orderView = (at: Spot) => (
    <OrderView
      spot={at}
      order={at.kind === "table" ? (openTables.get(at.table) ?? null) : null}
      draft={draftOf(at).lines}
      api={api}
      details={draftOf(at)}
      menu={menu}
      priced={priced}
      total={total}
      wide={wide}
      busy={busy}
      onBack={() => {
        setView(null);
        if (at.kind === "table") setTab("tables");
      }}
      onMove={() => {
        if (at.kind === "table") setView({ name: "move", spot: at });
      }}
      onDetails={(change) => {
        setDraft(at, (current) => ({ ...current, ...change }));
      }}
      onRemove={(key) => {
        setDraft(at, (current) => ({ ...current, lines: current.lines.filter((line: DraftLine) => line.key !== key) }));
      }}
      onQuantity={(key, delta) => {
        setDraft(at, (current) => ({ ...current, lines: changeQuantity(current.lines, key, delta) }));
      }}
      onPick={() => {
        setView({ name: "pick", spot: at, category: null, query: "" });
      }}
      onSend={() => {
        if (at.kind === "table") void send(at);
      }}
      onBill={() => {
        if (at.kind === "table") void printBill(at);
      }}
      onPay={() => {
        void openPay(at);
      }}
      onLater={() => {
        void later();
      }}
    />
  );

  const pickView = (at: Spot, category: string | null, query: string) => (
    <PickView
      menu={menu}
      channel={channelOf(at)}
      promotions={promotions}
      soldOut={soldOut}
      category={category}
      query={query}
      wide={wide}
      onCategory={(id) => {
        setLastCategory(id);
        setView({ name: "pick", spot: at, category: id, query: "" });
      }}
      onQuery={(typed) => {
        setView({ name: "pick", spot: at, category, query: typed });
      }}
      onBack={back}
      onProduct={(entry) => {
        onProduct(at, entry, category);
      }}
      onAllergens={() => {
        setAllergens(true);
      }}
    />
  );

  /** What the view shows (on an iPad: the right column). */
  const content = (current: View) => {
    switch (current.name) {
      case "order":
        return orderView(current.spot);
      case "pick":
        return pickView(current.spot, current.category ?? lastCategory, current.query);
      case "options":
        // the window over the products it was chosen from (Furkan, 19/10/2026)
        return (
          <>
            {pickView(current.spot, current.category ?? lastCategory, "")}
            <OptionsView
              key={`${current.product.id}:${current.variant ?? ""}`}
              api={api}
              menu={menu}
              rules={rules}
              product={current.product}
              variant={current.variant}
              channel={channelOf(current.spot)}
              promotions={promotions}
              onAdd={(line) => {
                addLine(current.spot, line);
              }}
              onBack={back}
            />
          </>
        );
      case "pay":
        return (
          <PayView
            api={api}
            spot={current.spot}
            order={current.spot.kind === "table" ? (openTables.get(current.spot.table) ?? null) : null}
            takeaway={
              current.spot.kind === "takeaway"
                ? {
                    lines: draftOf(current.spot).lines.map(toLineRequest),
                    name: draftOf(current.spot).name,
                    ...takeawayRequest(draftOf(current.spot)),
                    totalCents: total,
                  }
                : null
            }
            general={general}
            bankQrAutomatic={bankQrAutomatic}
            receiptQr={receiptQr}
            onBill={(answer, leave) => {
              showBill(answer);
              if (leave) {
                setView(null);
                setTab("tables");
              }
            }}
            staff={session.staff}
            deviceType={deviceType}
            onBack={back}
            onOrder={orders.upsert}
            onDone={() => {
              if (current.spot.kind === "takeaway") setDraft(current.spot, () => EMPTY_DRAFT);
              setView(null);
              setTab(current.spot.kind === "table" ? "tables" : "takeaway");
            }}
            toast={toast}
            report={report}
          />
        );
      case "move":
        return (
          <MoveView
            from={current.spot.table}
            floor={floor}
            openTables={openTables}
            withDraft={withDraft}
            busy={busy}
            onBack={back}
            onMove={(to) => {
              void move(current.spot.table, to);
            }}
          />
        );
    }
  };

  // iPad (prototype ipadO): the takeaway opens on the products at once, the bill stays on the left
  const shown: View | null =
    wide && view === null && tab === "takeaway"
      ? { name: "pick", spot: { kind: "takeaway" }, category: lastCategory, query: "" }
      : wide && view?.name === "order"
        ? { name: "pick", spot: view.spot, category: lastCategory, query: "" }
        : view;
  const twoColumns = wide && shown !== null;

  /** What a column shows: a new view (or another category) starts at the top. */
  const viewKey = (current: View | null) =>
    current === null
      ? tab
      : // the options open over the products: the list stays where it was
        `${current.name === "options" ? "pick" : current.name}:${spotKey(current.spot)}:${current.name === "pick" || current.name === "options" ? (current.category ?? "") : ""}`;

  let body;
  if (twoColumns) {
    body = (
      <div className="ipad2">
        <AppColumn className="ipad-l" scrollKey={spotKey(shown.spot)}>
          {orderView(shown.spot)}
        </AppColumn>
        <AppColumn className="ipad-r" scrollKey={viewKey(shown)}>
          {content(shown)}
        </AppColumn>
      </div>
    );
  } else if (shown !== null) {
    body = content(shown);
  } else if (tab === "home") {
    body = (
      <WaiterHome
        api={api}
        staff={session.staff}
        orders={orders.orders}
        onTable={(table) => {
          setTab("tables");
          setView({ name: "order", spot: { kind: "table", table } });
        }}
        onOrder={orders.upsert}
        toast={toast}
      />
    );
  } else if (tab === "tables") {
    body = (
      <TablesView
        floor={floor}
        openTables={openTables}
        withDraft={withDraft}
        selected={null}
        now={now}
        onTable={(table) => {
          setView({ name: "order", spot: { kind: "table", table } });
        }}
      />
    );
  } else {
    body = orderView({ kind: "takeaway" });
  }

  // the tabs at the bottom, each with its symbol (laid out like the example Furkan gave, 08/10/2026), floating over
  // the page as in Instagram, the field of the chosen one sliding to it (Furkan, 19/10/2026); the card on Start no
  // longer has its own Tafels and Afhaal ("dubbel")
  const nav =
    view !== null && !wide ? undefined : (
      <nav
        className="ph-nav"
        aria-label={t.tafoApp.title}
        style={{ "--at": (["home", "tables", "takeaway"] as const).indexOf(tab) } as CSSProperties}
      >
        {(
          [
            ["home", t.tafoApp.tabs.home, "bill", 0],
            ["tables", t.tafoApp.tabs.tables, "table", openTables.size],
            ["takeaway", t.tafoApp.tabs.takeaway, "bag", draftOf({ kind: "takeaway" }).lines.length],
          ] as const satisfies readonly (readonly [Tab, string, AppIconName, number])[]
        ).map(([key, title, icon, count]) => (
          <button
            key={key}
            type="button"
            aria-pressed={tab === key}
            onClick={() => {
              setTab(key);
              setView(null);
            }}
          >
            {/* the word first, so the button is called "Tafels 1"; the symbol stands above it (CSS) */}
            <span>{title}</span>
            <span className="ph-navicon">
              <AppIcon name={icon} />
              {count > 0 && <span className="badge">{count}</span>}
            </span>
          </button>
        ))}
      </nav>
    );

  return (
    <AppPage
      head={env.head}
      nav={nav}
      wide={wide}
      bodyClass={twoColumns ? "ipadb" : shown === null && tab === "home" ? "hm-page" : ""}
      scrollKey={twoColumns ? "ipad" : viewKey(shown)}
    >
      {pending > 0 && (
        <div className="banner bad" role="alert">
          <span>{t.errors.unconfirmed(pending)}</span>
          <button type="button" className="btn small" onClick={() => void replay()}>
            {t.errors.retryNow}
          </button>
        </div>
      )}
      {body}
      {bill && (
        <Dialog
          title={t.tafoApp.bill.title(bill.order.table ?? "")}
          onClose={() => {
            setBill(null);
          }}
        >
          <div className="bonwrap">
            <Bill
              order={bill.order}
              shop={status.shop}
              general={general}
              waiter={session.staff.name}
              at={bill.at}
              parts={bill.parts}
            />
          </div>
          <p className="note">
            {(() => {
              const printed = receiptPrinter(bill.prints, "bill");
              return printed === null
                ? t.tafoApp.bill.noPrinter
                : t.tafoApp.bill.sentTo(printingPc(bill.prints), printed.printer);
            })()}
          </p>
        </Dialog>
      )}
      {allergens && (
        <AllergenCard
          menu={menu}
          onClose={() => {
            setAllergens(false);
          }}
        />
      )}
    </AppPage>
  );
}
